import React, { Fragment, useState, FC, Suspense, useEffect } from 'react';
import {
  Text,
  Box,
  SideSheet,
  SideSheetHeader,
  SideSheetClose,
  SideSheetBody,
  SideSheetFooter,
  SideSheetSubHeader,
  SideSheetContentCol,
  Spinner,
  SpinnerSize,
} from '@bechtel/gds';
import { Formik, FormikProps } from 'formik';
import { object, string } from 'yup';
import { SectionHeader, ErrorBoundary, SuspenseLoader, ErrorRetry } from '@bechtel/client-common';
import { useCrewMember } from '@bechtel/client-weld-common';
import { FooterButtonStyled } from './styled';
import { t, Trans } from '@lingui/macro';
import { FormikState, SingleCloseRequestProps } from './types';
import { RodRoomAttendantSelector } from '~/components/molecules';
import { CrewMember } from '@bechtel/types';
import { RodRequestComments } from '@bechtel/client-weld-common';
import { CLOSE_ROD_REQUEST } from '@bechtel/client-gql';
import { useMutation, useApolloClient } from '@apollo/client';
import { CloseRodRequestInput, CloseRodRequestPayload, PersonaType, CrewMemberList } from '@bechtel/types';
import { get } from 'lodash';
import { CloseRodRequestError, CloseRodRequestErrorType } from './error';
export const SingleCloseRequest: FC<SingleCloseRequestProps> = ({ onClose, rodRequest }) => {
  const initialValues: FormikState = {
    comments: '',
  };
  const client = useApolloClient();
  const [signedBy, setSignedBy] = useState<CrewMember | undefined>(undefined);
  const [closeRodRequestError, setCloseRodRequestError] = useState<CloseRodRequestErrorType | null>(null);
  const [closeRequest, { loading: mutationLoading }] = useMutation<
    { closeRodRequest: CloseRodRequestPayload },
    { input: CloseRodRequestInput }
  >(CLOSE_ROD_REQUEST, {
    onCompleted: (data) => {
      const error = get(data, 'closeRodRequest.error');
      if (error) {
        setCloseRodRequestError(error);
      } else {
        client.refetchQueries({
          include: ['rodRequests', 'rodRoomStatsSummary'],
        });
        setCloseRodRequestError(null);
        onClose(true);
      }
    },
    onError: (error) => {
      if (error) {
        setCloseRodRequestError(error);
      }
    },
  });

  const validationSchema = object().shape({
    comments: string()
      .trim()
      .min(2, t`Too Short!`)
      .max(2000, t`Comment too large`)
      .required(t`Comment is mandatory`),
  });
  const [
    getAttendantList,
    { data: attendantData, error: attendantError, loading: attendantLoading, refetch: attendantRefetch },
  ] = useCrewMember();
  useEffect(() => {
    getAttendantList({
      variables: {
        input: {
          persona: PersonaType.ROD_ROOM_ATTENDANT,
        },
        paging: {
          cursor: '1',
          limit: 20,
        },
      },
    });
  }, [getAttendantList]);
  const attendantList = get(attendantData, 'crewMembers.items', []) as CrewMemberList['items'];
  return (
    <>
      <Formik
        enableReinitialize={true}
        validationSchema={validationSchema}
        initialValues={initialValues}
        onSubmit={async (values: FormikState) => {
          const closeRodRequestInput: CloseRodRequestInput = {
            rodRequestId: rodRequest?.id,
            closedByUserId: signedBy?.id ? signedBy?.id : '',
            comments: values.comments,
          };
          await closeRequest({
            variables: {
              input: closeRodRequestInput,
            },
          });
        }}
      >
        {(props: FormikProps<FormikState>) => {
          const { handleSubmit, dirty, isValid } = props;
          return (
            <Fragment>
              <SideSheet onDismiss={() => onClose(false)} visible data-testid="close-request-container">
                <SideSheetHeader title={t`Close Request`}>
                  <SideSheetClose onClick={() => onClose(false)}></SideSheetClose>
                </SideSheetHeader>
                <SideSheetSubHeader>
                  <Text color="grey.1000" size="500" family="graphik">
                    {rodRequest.requestNumber}
                  </Text>
                  <Box mt={{ sm: '300' }}>
                    <Text color="grey.700" size="300">
                      {rodRequest?.weldProcedure ? rodRequest?.weldProcedure?.procedure : ''}
                    </Text>
                  </Box>
                </SideSheetSubHeader>
                <SideSheetBody data-testid="close-request-body">
                  <SideSheetContentCol>
                    <SectionHeader title={t`Mention Reason `} />
                    <RodRequestComments fieldName="comments" />
                    <SectionHeader title={t`Closed by`} />
                    <Box px={{ sm: '500' }} pt={{ sm: '600' }}>
                      {!attendantLoading && attendantError && (
                        <div data-testid="welder-error">
                          <ErrorRetry refetch={attendantRefetch}>
                            <Trans>There was a problem loading attendant list</Trans>
                          </ErrorRetry>
                        </div>
                      )}
                      {!attendantError && (
                        <RodRoomAttendantSelector
                          selectedItem={signedBy}
                          onRodRoomAttendantSelection={(selectedItem) => {
                            setSignedBy(selectedItem);
                          }}
                          items={attendantList}
                        />
                      )}
                    </Box>
                  </SideSheetContentCol>
                </SideSheetBody>
                <SideSheetFooter>
                  <Box justifyContent={{ sm: 'flex-end' }}>
                    <Box mr={{ sm: '400' }}>
                      <FooterButtonStyled data-testid="request-cancel" onClick={() => onClose(false)}>
                        <Trans> Cancel</Trans>
                      </FooterButtonStyled>
                    </Box>
                    {mutationLoading ? (
                      <Spinner data-testid="mutation-loading" size={SpinnerSize.SMALL} />
                    ) : (
                      <FooterButtonStyled
                        data-testid="request-close"
                        disabled={!dirty || !isValid || !signedBy?.id}
                        onClick={() => handleSubmit()}
                      >
                        <Trans>Close Request</Trans>
                      </FooterButtonStyled>
                    )}
                  </Box>
                </SideSheetFooter>
              </SideSheet>
            </Fragment>
          );
        }}
      </Formik>
      {closeRodRequestError && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <CloseRodRequestError error={closeRodRequestError} close={() => setCloseRodRequestError(null)} />
          </Suspense>
        </ErrorBoundary>
      )}
    </>
  );
};
